import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  ExtraValidators,
  FormBySchema,
  FormFieldLayout,
  isTmpId,
  useAPIWrapper,
} from 'drf-react-by-schema';

import { useAppSelector } from 'src/app/hooks';
import { appDispatch, getAzeiteOrOleoKey, getAzeiteOrOleoName } from 'src/utils/utils';
import { setPage } from 'src/features/pageSlice';
import { AzeiteOrOleoProductionStatus, GENERAL_ADMINS, ProductionKey } from 'src/constants';
import { PageType } from 'src/constants/PageType';
import { formLayout } from 'src/styles/layout';
import { useHasPermissionsMulti } from 'src/hooks/useHasPermissions';

const MODEL = 'azeite_or_oleo_production';

const QUEBRA_E_PESAGEM_SECTION: FormFieldLayout = {
  title: 'Quebra e pesagem das amêndoas',
  rows: ['data_quebra', 'perfil', 'ajudantes', 'peso_amendoas'],
};

const ARMAZENAMENTO_SECTION: FormFieldLayout = {
  title: 'Produção e armazenamento',
  rows: ['data_armazenamento', 'volume_produzido'],
};

export default function AzeiteOrOleoProductionForm() {
  const { id, key, type, formValues, perfil } = useAppSelector((state) => state.page);
  const [isAdmin] = useHasPermissionsMulti([GENERAL_ADMINS]);
  const { pageForm, loadSinglePageData, onEditModel } = useAPIWrapper();
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors, isDirty },
    register,
  } = useForm({
    mode: 'onBlur',
    resolver: pageForm?.validationSchema ? yupResolver(pageForm.validationSchema) : undefined,
  });

  const hiddenFields = ['id', 'status', 'azeite_or_oleo'];

  const azeiteOrOleo = useMemo(() => getAzeiteOrOleoKey(key), [key]);

  const status = useMemo(() => {
    return formValues?.status?.value as AzeiteOrOleoProductionStatus | undefined;
  }, [formValues?.status]);

  const fieldsLayout = useMemo(() => {
    if (!status || !azeiteOrOleo) {
      return [QUEBRA_E_PESAGEM_SECTION];
    }
    const newFieldsLayout: FormFieldLayout[] = [];
    if (type === PageType.CREATE || status === AzeiteOrOleoProductionStatus.COMPLETE) {
      newFieldsLayout.push({
        ...QUEBRA_E_PESAGEM_SECTION,
        customLabels: {
          peso_amendoas:
            azeiteOrOleo === ProductionKey.AZEITE
              ? 'Peso das amêndoas torradas'
              : 'Peso das amêndoas cruas',
        },
      });
    }
    if (type === PageType.EDIT) {
      newFieldsLayout.push(ARMAZENAMENTO_SECTION);
    }
    return newFieldsLayout;
  }, [status, type, azeiteOrOleo]);

  useEffect(() => {
    if (!key || !fieldsLayout) {
      return;
    }
    const azeiteOrOleoName = getAzeiteOrOleoName({ pageKey: key });
    if (!perfil || !azeiteOrOleo || !type || (type === PageType.EDIT && !id)) {
      return;
    }
    const loadAllDataAsync = async () => {
      const defaults =
        type === PageType.EDIT
          ? {
              data_armazenamento: new Date(),
            }
          : {
              ...(isAdmin ? {} : { perfil }),
              azeite_or_oleo: azeiteOrOleo,
            };
      const extraValidators: ExtraValidators | undefined =
        type === PageType.EDIT
          ? {
              volume_produzido: Yup.number().required(
                `Quanto você produziu de ${azeiteOrOleoName}?`
              ),
            }
          : undefined;
      const pageTitle = getAzeiteOrOleoName({ pageKey: key, title: true });
      const values = await loadSinglePageData({
        model: MODEL,
        objId: id,
        defaults,
        extraValidators,
        fieldsLayout,
      });
      if (values) {
        const submitSuccessTarget = `/${key}/view/${isTmpId(id) ? '{ID}' : id}/?type=${isTmpId(id) ? PageType.CREATE : values.status.value !== AzeiteOrOleoProductionStatus.COMPLETE ? 'completed' : PageType.EDIT}`;

        reset(values);

        if (type === PageType.EDIT && values.status !== AzeiteOrOleoProductionStatus.COMPLETE) {
          setValue('status', { value: AzeiteOrOleoProductionStatus.COMPLETE });
        }

        appDispatch(
          setPage({
            model: MODEL,
            title: pageTitle,
            formValues: values,
            submitSuccessTarget,
          })
        );
      }
    };
    loadAllDataAsync();
  }, [id, perfil, key, type, fieldsLayout, isAdmin]);

  useEffect(() => {
    appDispatch(
      setPage({
        formDisabled: !isDirty,
      })
    );
  }, [isDirty]);

  useEffect(() => {
    appDispatch(
      setPage({
        handleSubmit,
      })
    );
  }, [handleSubmit]);

  if (!pageForm?.schema || !formValues || !key || !type || !fieldsLayout) {
    return null;
  }

  return (
    <FormBySchema
      fieldsLayout={fieldsLayout}
      hiddenFields={hiddenFields}
      control={control}
      register={register}
      errors={errors}
      schema={pageForm.schema}
      onEditModel={onEditModel}
      getValues={getValues}
      setValue={setValue}
      elevation={0}
      {...formLayout}
    />
  );
}
